<template>
  <div class="post-content">
    <div class="row row--flex">
      <div class="col-70">
        <p>End of the year, unexpected surplus budget, a trade fair visit, or just a tendency for stocking up are among the most common reasons for production or reprints. This kind of pressure does not have a positive impact on our realistic judgment. Ordered materials pile up in magazines, long forgotten, still waiting for the moment when they become useful. And so I encourage you to start with asking yourself a question: is this production order really necessary? This question is especially important when designing promotional merchandise. There are plenty of innovative solutions available in this market, and yet most clients still choose the most popular, not always necessary but safe standard items, such as USB sticks, pens, and lanyards.</p>
      </div>
      <Sidenote class="col-30">
        <p>I encourage you to start with asking yourself a question: is this production order really necessary?</p>
      </Sidenote>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">When ordering promotional merchandise, always make sure:</p>
        <ul>
          <li>Do I really need this product?</li>
          <li>Can I afford to produce merchandise of quality matching the values and image of my brand?</li>
          <li>Who will use those items and why?</li>
          <li>Do I really need them in such a large amount?</li>
          <li>Is this product imported? If so, what justifies the carbon footprint it generates?</li>
          <li>Does my budget allow swapping this product for a smaller batch of genuinely valuable gifts that bring in a better message or story?</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>
          For example, take a small production of 300 metal USB sticks. Let’s say we choose a premium-quality product with 4GB capacity and custom etching. Such an order will cost about
          <span
            class="red"
          >€XXX</span>. In the same price range, you can order 100 books on your brand-related subject and add a bespoke message inside. Another reliable and useful gift idea is a high-quality notebook.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p
          class="indend-left highlighted-text"
        >In the market, you can find a multitude of solutions that are made both locally and sustainably. Many brands offer a variety of ways to personalise their products. And if this made you think of a surplus of calendars in your desk drawer or the world in general – well done!</p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>
          So, back to the first questions: do I really need this product? And also: when you open your drawer, can you see more unused calendars or USB sticks with logos of companies with whom you have no relationship?
          <span
            class="text-background"
          >Turning to local products is not just a trend but a real change, forced by climate change and the global crisis caused by the coronavirus.</span> And so we choose our products to be manufactured near our location, for want of more sustainable production, availability and quality of the products and supporting the local businesses. It is also essential to keep in mind that a local product (being more than just handicraft or some regional foods) showcases the authenticity of the brand and its intentions. Local products are items manufactured in your area, by local businesses or artisans. It is also worth looking for inspiration online. Don’t be afraid to go for unexpected solutions. Another example of sustainable merchandise is durable publications and functional paper gadgets that are more original than mass-produced items and can be recycled once they fulfil their initial purpose.
        </p>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p class="heading--sans-serif">Questions to ask yourself before you print new merchandise:</p>
        <ul>
          <li>At whom are those materials directed, and what information does the recipient expect of them?</li>
          <li>Can I do something to ensure the recipient interacts with my materials more than once?</li>
          <li>Can I avoid making it a single-use product?</li>
          <li>Is the distribution strategy for this product coherent with how I wish for my products/services to be perceived?</li>
        </ul>
      </div>
    </div>
    <div class="row">
      <div class="col-70">
        <p>Enormous print runs of leaflets and flyers often have no practical justification. Purposeful, well-planned communication strategy in an appropriately chosen form and shape can be incomparably more effective.</p>
      </div>
    </div>
    <div class="row row--flex">
      <div class="col-70">
        <p class="heading--sans-serif">Examples:</p>
        <ul class="examples">
          <li>leaflet advertising a local business</li>
          <li>personalised letter to local community members with discount offer included</li>
          <li>leaflet advertising a local restaurant</li>
          <li>fridge-magnet business card with necessary information</li>
          <li>full catalogue of products offered</li>
          <li>carefully designed catalogue showcasing offers services and solutions, inviting the client to view the full offer online</li>
          <li>yet another calendar</li>
          <li>notebook with brand-related photographs</li>
        </ul>
      </div>
      <div class="col-30">
        <RelatedPosts :currentPostID="postID" />
      </div>
    </div>
    <ScrollToTop />
  </div>
</template>

<script>
import RelatedPosts from "../RelatedPosts";
import ScrollToTop from "../ScrollToTop";
import Sidenote from "../Sidenote";

export default {
  name: "Post1",
  components: {
    RelatedPosts,
    ScrollToTop,
    Sidenote
  },
  props: {
    postID: Number
  }
};
</script>

<style lang="scss" scoped>
.examples {
  li {
    color: $black-color;
    &:not(:last-child) {
      margin-bottom: 20px;
    }
    &:nth-child(odd) {
      padding-left: 0;
      color: $primary-color;
      &::before {
        display: none;
      }
    }
  }
}
</style>